import React, { useMemo } from "react";
import styled from "styled-components";
import Draft from "draft-js";
import { SHOW_DEBUG_OVERLAY } from "@sonder/editor/source/core/constants";
import { getHtmlFromState } from "@sonder/editor/source/core/helpers";
import {
  useEditorStore,
  useSearchTermStore,
} from "@sonder/editor/source/core/stores";

const Section = styled.div`
  font-family: monospace;
  margin: 0;
  overflow: auto;
  padding: 1rem;
  white-space: break-spaces;
`;
const SectionHeader = styled.h3`
  margin: 0;
  position: sticky;
  top: 0;
`;
const Wrapper = styled.div`
  bottom: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  left: 0;
  margin: 0 auto;
  position: fixed;
  right: 0;
  z-index: 100;

  background: #ffffff9f;
  box-sizing: border-box;
  font-size: 10px;
  height: 50vh;
  text-align: left;

  > ${Section}:not(:last-child) {
    border-right: 1px solid #fffa;
  }
`;

const getSelectionData = (state: Draft.EditorState) => {
  const content = state.getCurrentContent();
  const selection = state.getSelection();

  const startKey = selection.getStartKey();
  const startOffset = selection.getStartOffset();
  const endKey = selection.getEndKey();
  const endOffset = selection.getEndOffset();
  const startBlock = content.getBlockForKey(startKey);
  const endBlock = content.getBlockForKey(endKey);
  const startEntityKey = startBlock.getEntityAt(startOffset);
  const endEntityKey = endBlock.getEntityAt(endOffset);
  const isBackward = selection.getIsBackward();
  const isCollapsed = selection.isCollapsed();
  const hasFocus = selection.getHasFocus();
  const startEntityType =
    startEntityKey && content.getEntity(startEntityKey).getType();
  const startEntityData =
    startEntityKey && content.getEntity(startEntityKey).getData();
  const endEntityType =
    endEntityKey && content.getEntity(endEntityKey).getType();
  const endEntityData =
    endEntityKey && content.getEntity(endEntityKey).getData();

  return {
    start: {
      block: startKey,
      offset: startOffset,
      entity: {
        key: startEntityKey,
        type: startEntityType,
        data: startEntityData,
      },
    },
    end: {
      block: endKey,
      offset: endOffset,
      entity: {
        key: endEntityKey,
        type: endEntityType,
        data: endEntityData,
      },
    },
    isBackward,
    isCollapsed,
    hasFocus,
  };
};

const prettyPrint = (obj: any) => JSON.stringify(obj, null, 2);

export const Debug = () => {
  const { state: editorState } = useEditorStore();
  const { state: searchTermState } = useSearchTermStore();
  const showDebugOverlay = localStorage.getItem(SHOW_DEBUG_OVERLAY);

  return useMemo(
    () =>
      showDebugOverlay === "true" ? (
        <Wrapper>
          <Section>
            <SectionHeader>Selection info</SectionHeader>
            {prettyPrint(getSelectionData(editorState))}
          </Section>
          <Section>
            <SectionHeader>Raw content</SectionHeader>
            {prettyPrint(Draft.convertToRaw(editorState.getCurrentContent()))}
          </Section>
          <Section>
            <SectionHeader>Search term info</SectionHeader>
            {prettyPrint(searchTermState)}
            <SectionHeader>HTML content</SectionHeader>
            {getHtmlFromState(editorState)}
            <SectionHeader>Plain content</SectionHeader>
            {editorState.getCurrentContent().getPlainText()}
          </Section>
        </Wrapper>
      ) : null,
    [editorState, searchTermState, showDebugOverlay],
  );
};
